import {
  putLabelsRbacEnrolment,
  useGetLabelsRbacEnrolment,
} from '@ipfabric/api-client/client/model/labels/labels';
import {
  deleteDataPoliciesId,
  postDataPolicies,
  putDataPoliciesId,
  useGetDataPolicies,
  useGetDataPoliciesImpact,
} from '@ipfabric/api-client/client/model/rbac-—-data-policies/rbac-—-data-policies';
import type {
  GetDataPolicies200Item,
  GetLabelsRbacEnrolment200Item,
  PostDataPoliciesBody,
  PutLabelsRbacEnrolmentBody,
} from '@ipfabric/api-client/client/schemas';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useQueryClient } from '@tanstack/react-query';

export type DataPolicy = GetDataPolicies200Item;
export type DataPolicyBody = PostDataPoliciesBody;
export type LabelEnrolment = GetLabelsRbacEnrolment200Item;

export const useDataPoliciesQuery = () => useGetDataPolicies();

export const useDataPoliciesImpactQuery = (snapshotId: string | null) =>
  useGetDataPoliciesImpact(
    { snapshotId: snapshotId ?? '' },
    { query: { enabled: snapshotId !== null } },
  );

export const useLabelEnrolmentQuery = (snapshotId: string | null) =>
  useGetLabelsRbacEnrolment(
    { snapshotId: snapshotId ?? '' },
    { query: { enabled: snapshotId !== null } },
  );

// Generated query keys start with the URL, so invalidate by URL prefix to hit every param set.
const useInvalidateUrls = (urls: readonly string[]) => {
  const queryClient = useQueryClient();
  return useStableCallback(async () => {
    await Promise.all(
      urls.map((url) => queryClient.invalidateQueries({ queryKey: [url] })),
    );
  });
};

const DATA_POLICY_URLS = [
  '/data-policies',
  '/data-policies/impact',
  '/labels/rbac-enrolment',
] as const;

const ENROLMENT_URLS = [
  '/labels/rbac-enrolment',
  '/labels',
  // Un-enrolling a label in use strips it from policies.
  '/data-policies',
  '/data-policies/impact',
] as const;

const useInvalidateDataPolicies = () => useInvalidateUrls(DATA_POLICY_URLS);

export const useDataPoliciesApi = () => {
  const invalidate = useInvalidateDataPolicies();

  const createDataPolicy = useStableCallback(async (body: DataPolicyBody) => {
    await postDataPolicies(body);
    await invalidate();
  });

  const updateDataPolicy = useStableCallback(
    async (id: string, body: DataPolicyBody) => {
      await putDataPoliciesId(id, body);
      await invalidate();
    },
  );

  const deleteDataPolicy = useStableCallback(async (id: string) => {
    await deleteDataPoliciesId(id);
    await invalidate();
  });

  return { createDataPolicy, updateDataPolicy, deleteDataPolicy };
};

export const useSaveLabelEnrolment = () => {
  const invalidate = useInvalidateUrls(ENROLMENT_URLS);
  return useStableCallback(async (body: PutLabelsRbacEnrolmentBody) => {
    await putLabelsRbacEnrolment(body);
    await invalidate();
  });
};
